import { IonButton, IonButtons, IonCol, IonContent, IonIcon, IonInput, IonItem, IonLabel, IonModal, IonRow, IonTitle, IonToolbar } from "@ionic/react";
import React, { useEffect, useState } from "react";
import ErrorBanner from "../Banners/ErrorBanner";
import DragDropFileUpload from "../FileBrowser/DragDropFileUpload";
import Spinner from "../Spinners/Spinner";
import { getExtension } from "../../utilities/files/File";
import { Horse } from "../../models";
import { Storage } from "aws-amplify";
import RequiredInputIndicator from "../Forms/RequiredInputIndicator";
import { close, cloudUploadOutline, eyeOutline, trash } from "ionicons/icons";
import ErrorAlert from "../Errors/ErrorAlert";
import "./FileIdentification.css";

interface _Props {
    horse?: Horse | null;
    isRequired?: Boolean | null;
    isDisabled?: Boolean | null;
    onSubmit: (file: File, title?: string) => void;
    onDelete?: () => void;
}

const CogginsDocumentForm: React.FC<_Props> = ({ horse, isRequired, isDisabled, onSubmit, onDelete }) => {
    const [document, setDocument] = useState<File>();
    const [title, setTitle] = useState("");
    const [url, setURL] = useState<any>();
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [modalError, setModalError] = useState("");

    useEffect(() => {
        setDocument(undefined);
        setError("");
        setModalError("");
    }, []);

    useEffect(() => {
        if (horse && horse.coggins) {
            handleGetDocument(horse.coggins.key);
        } else if (horse && !horse.coggins) {
            setURL(undefined);
        } else {
            // Clear URL when coggins is deleted or horse is null
            setURL(undefined);
        }
    }, [horse, horse?.coggins]);

    async function handleGetDocument(fileName: string) {
        try {
            const url = await Storage.get(fileName);
            setURL(url);
            // Reset confirming state after document is loaded
            if (isConfirming) {
                setIsConfirming(false);
            }
        } catch (error: any) {
            // Reset confirming state even on error
            if (isConfirming) {
                setIsConfirming(false);
            }
            return "";
        }
    }

    const handleSelectedFile = (file: File) => {
        setIsLoading(true);
        try {
            // Check if file is PNG, PDF, or JPEG only
            const fileName = file.name.toLowerCase();
            const isPng = fileName.endsWith(".png");
            const isPdf = fileName.endsWith(".pdf");
            const isJpeg = fileName.endsWith(".jpg") || fileName.endsWith(".jpeg");

            if (isPng || isPdf || isJpeg) {
                setDocument(file);
                setTitle(file.name);
                setModalError("");
            } else {
                setModalError("Please check that your upload is one of: .pdf, .jpg, .jpeg, .png");
            }
        } catch (error: any) {}
        setIsLoading(false);
    };

    const handleUseFileClick = () => {
        if (document) {
            setIsConfirming(true);
            onSubmit(document, title);
            setModalError("");
            setTitle("");
            setDocument(undefined);
            setShowModal(false);
        } else {
            setModalError("Please include a file.");
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setModalError("");
        setTitle("");
        setDocument(undefined);
    };

    const handleRemoveFileClick = () => {
        if (onDelete) {
            onDelete();
            // Clear all local state when file is deleted
            setURL(undefined);
            setError("");
            setDocument(undefined);
            setTitle("");
            setModalError("");
        }
    };

    const handleViewDocument = () => {
        if (url) {
            window.open(url, "_blank");
        }
    };

    const getFileTypeIcon = (fileName: string) => {
        const extension = getExtension(fileName).toLowerCase();
        switch (extension) {
            case "pdf":
                return "📄";
            case "png":
            case "jpg":
            case "jpeg":
                return "🖼️";
            default:
                return "📄";
        }
    };

    return (
        <>
            <form>
                <hr />
                <h4>
                    <b>Coggins Document</b> {isRequired && <RequiredInputIndicator />}
                </h4>
                {error && (
                    <IonRow>
                        <IonCol>
                            <ErrorBanner error={error} />
                        </IonCol>
                    </IonRow>
                )}
                {isLoading ? (
                    <IonRow>
                        <IonCol>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                                <Spinner />
                                <span>Uploading your file...</span>
                            </div>
                        </IonCol>
                    </IonRow>
                ) : isConfirming ? (
                    <IonRow>
                        <IonCol>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                                <Spinner />
                                <span>Processing your file...</span>
                            </div>
                        </IonCol>
                    </IonRow>
                ) : horse && horse.coggins && url ? (
                    <IonRow>
                        <IonCol size="12" className="m-0">
                            <div className="existing-file-container" onClick={handleViewDocument} style={{ cursor: "pointer" }}>
                                <div className="file-identification-container m-0">
                                    <div className="file-icon-container">
                                        <div className="file-icon">
                                            <span className="file-type-icon">{horse.coggins.key ? getFileTypeIcon(horse.coggins.key) : "📄"}</span>
                                        </div>
                                    </div>
                                    <div className="file-name">{horse.coggins.description || "Coggins Document"}</div>
                                    <div className="file-actions m-0">
                                        <IonButton
                                            color="tertiary"
                                            fill="clear"
                                            size="default"
                                            className="eye-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewDocument();
                                            }}>
                                            <IonIcon icon={eyeOutline} />
                                        </IonButton>
                                        {!!onDelete && (
                                            <IonButton
                                                color="danger"
                                                fill="clear"
                                                size="default"
                                                className="trash-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveFileClick();
                                                }}>
                                                <IonIcon className="card-icon-size-2 text-danger" icon={trash} />
                                            </IonButton>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </IonCol>
                    </IonRow>
                ) : null}
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12" className="ion-text-center">
                        <IonButton
                            color="tertiary"
                            onClick={() => {
                                // Clear any stale state when opening modal
                                setDocument(undefined);
                                setTitle("");
                                setModalError("");
                                setShowModal(true);
                            }}
                            expand="block"
                            className="ion-button-wrap">
                            <IonIcon icon={cloudUploadOutline} slot="start" />
                            Upload New File
                        </IonButton>
                    </IonCol>
                </IonRow>
                <hr />
            </form>

            <IonModal backdropDismiss={false} isOpen={showModal} id="cogginsModal">
                <IonToolbar color="light">
                    <IonTitle className="ion-text-center">Upload file</IonTitle>
                    <IonButtons slot="end">
                        <IonButton fill="clear" onClick={handleCloseModal}>
                            <IonIcon icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                <IonContent className="ion-padding" style={{ paddingBottom: "100px" }}>
                    {modalError && <ErrorAlert width="12" error={modalError} />}

                    {/* Drag and Drop Upload Area */}
                    <IonRow className="ion-justify-content-center">
                        <IonCol size="12">
                            <DragDropFileUpload
                                onFileSelect={handleSelectedFile}
                                onFileRemove={() => {
                                    setDocument(undefined);
                                    setTitle("");
                                    setModalError("");
                                }}
                                acceptedTypes={[".png", ".pdf", ".jpeg", ".jpg"]}
                                maxSize={25}
                            />
                        </IonCol>
                    </IonRow>

                    {/* Title Input - Only show when file is selected */}
                    {document && (
                        <>
                            <IonRow className="ion-margin-top">
                                <IonCol size="12">
                                    <IonItem>
                                        <IonLabel position="floating">Change Title (Optional)</IonLabel>
                                        <IonInput
                                            type="text"
                                            value={title}
                                            aria-required={true}
                                            clearInput
                                            onIonChange={(e) => {
                                                setTitle(e.detail.value!);
                                            }}
                                        />
                                    </IonItem>
                                </IonCol>
                            </IonRow>
                        </>
                    )}
                </IonContent>

                {/* Footer with Help and Action Buttons - Fixed at bottom */}
                <div
                    className="modal-footer ion-justify-content-center ion-align-items-center"
                    style={{
                        position: "sticky",
                        bottom: "0",
                        left: "0",
                        right: "0",
                        padding: "1rem",
                        borderTop: "1px solid #e5e7eb",
                        backgroundColor: "#f9fafb",
                        zIndex: 1000,
                    }}>
                    {document && (
                        <IonButton color="success" onClick={() => handleUseFileClick()}>
                            Confirm
                        </IonButton>
                    )}
                </div>
            </IonModal>
        </>
    );
};

export default CogginsDocumentForm;
